import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  type GroupBase,
  type MultiValue,
  type MultiValueRemoveProps,
  type OptionProps,
  components as reactSelectComponents,
  type SelectInstance,
  type SingleValue,
} from 'react-select';
import { useMount } from 'react-use';

import {
  CreatableSelect,
  InfoTipIcon,
  RevelioTooltip,
  Select,
  Text,
} from '@revelio/ui';

import { shouldShowCurrentPreviousToggle } from '../../config/currentPreviousConfig';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import { FilterId, type FreeTextFilter } from '../../types/filters';
import { FilterSelectFooter } from '../FilterSelectFooter';
import { ToggleSelect } from '../ToggleSelect';
import styles from './filterSelectTheme.module.css';
import layoutStyles from './free-text-filter-form.module.css';

interface FreeTextFilterProps {
  filter: FreeTextFilter;
  closeMenu: () => void;
  onSave: (filter: FreeTextFilter) => void;
  onBack?: () => void;
  isNewInstance?: boolean;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
}

interface KeywordOption {
  label: string;
  value: string;
}

interface AnyOfNoneOfOption {
  label: string;
  displayLabel: string;
  value: boolean;
  showDefault?: boolean;
}

interface InlineAnyOfNoneOfDropdownProps {
  isNot: boolean;
  onChange: (isNot: boolean) => void;
}

const anyOfNoneOfOptions: AnyOfNoneOfOption[] = [
  {
    label: 'any of',
    displayLabel: 'any of',
    value: false,
    showDefault: true,
  },
  {
    label: 'none of',
    displayLabel: 'none of',
    value: true,
  },
];

const CustomAnyOfNoneOfOption = (
  props: OptionProps<AnyOfNoneOfOption, false>
) => {
  const { data, innerRef, innerProps, isFocused } = props;
  return (
    <div
      ref={innerRef}
      className={classNames(
        styles.optionRow,
        isFocused && styles.optionRowFocused
      )}
      {...innerProps}
    >
      <Text as="span" fontSize="x-sm" className={styles.optionLabel}>
        {data.label}
      </Text>
      {data.showDefault && (
        <Text as="span" fontSize="x-sm" className={styles.optionDefault}>
          Default
        </Text>
      )}
    </div>
  );
};

const InlineAnyOfNoneOfDropdown = ({
  isNot,
  onChange,
}: InlineAnyOfNoneOfDropdownProps) => {
  const handleChange = (selected: SingleValue<AnyOfNoneOfOption>) => {
    if (selected) {
      onChange(selected.value);
    }
  };

  const formatOptionLabel = ({ displayLabel }: AnyOfNoneOfOption) => {
    return displayLabel;
  };

  return (
    <div className={layoutStyles.inlineDropdown}>
      <Select<AnyOfNoneOfOption>
        className={layoutStyles.anyOfNoneOfSelect}
        options={anyOfNoneOfOptions}
        value={anyOfNoneOfOptions.find((opt) => opt.value === isNot) || null}
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        size="sm"
        variant="ghost"
        isSearchable={false}
        // The prefix marks the portaled menu for the owning popover's
        // interact-outside guard.
        classNamePrefix="any-of-none-of-select"
        components={{ Option: CustomAnyOfNoneOfOption }}
      />
    </div>
  );
};

interface KeywordSelectProps {
  keywords: readonly KeywordOption[];
  onKeywordsChange: (keywords: readonly KeywordOption[]) => void;
  placeholder: string;
  isNot?: boolean;
  onIsNotChange?: (isNot: boolean) => void;
  showNotToggle?: boolean;
}

/* A thin stroke cross, a touch under the Chakra Tag close button's size; the
   react-select default is a filled glyph and reads heavier. */
const KeywordRemove = (
  props: MultiValueRemoveProps<KeywordOption, true, GroupBase<KeywordOption>>
) => (
  <reactSelectComponents.MultiValueRemove {...props}>
    <IconCrossLarge size={14} />
  </reactSelectComponents.MultiValueRemove>
);

const KeywordSelect = ({
  keywords,
  onKeywordsChange,
  placeholder,
  isNot = false,
  onIsNotChange,
  showNotToggle = false,
}: KeywordSelectProps) => {
  const [inputState, setInputState] = useState('');
  const innerRef =
    useRef<SelectInstance<KeywordOption, true, GroupBase<KeywordOption>>>(null);

  useMount(() => {
    if (innerRef.current) {
      innerRef?.current?.focus();
    }
  });

  const handleChange = (selectedOptions: MultiValue<KeywordOption>) => {
    const validOptions = selectedOptions.filter(
      (option) => option.value.trim().length > 0
    );
    onKeywordsChange(validOptions);
  };

  const handleInputChange = (inputValue: string) => {
    setInputState(inputValue);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const paste = event.clipboardData.getData('text');
    const pastedValues = paste.split(',').map((val) => val.trim());
    const newOptions = pastedValues
      .filter((value) => value.length > 0)
      .map((value) => ({
        value,
        label: value,
      }));
    onKeywordsChange([...keywords, ...newOptions]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ',') {
      event.preventDefault();
      if (inputState.trim()) {
        const newOption = {
          label: inputState.trim(),
          value: inputState.trim(),
        };
        onKeywordsChange([...keywords, newOption]);
        setInputState('');
      }
    }
  };

  const isValidNewOption = (inputValue: string) => {
    return inputValue.trim().length > 0;
  };

  return (
    <div className={layoutStyles.keywordRow}>
      <div className={layoutStyles.keywordLabel}>
        <Text fontSize="sm" className={layoutStyles.keywordText}>
          Contains{' '}
          {showNotToggle && onIsNotChange ? (
            <InlineAnyOfNoneOfDropdown isNot={isNot} onChange={onIsNotChange} />
          ) : (
            <Text as="span" fontSize="sm" className={styles.muted600}>
              any of
            </Text>
          )}
        </Text>
        <RevelioTooltip
          label="Type or paste a list of comma separated keywords"
          placement="top"
        >
          <InfoTipIcon size={12} />
        </RevelioTooltip>
      </div>
      <div className={layoutStyles.keywordInput}>
        <div data-testid="keyword-input-box" onPaste={handlePaste}>
          <CreatableSelect<KeywordOption, true>
            ref={innerRef}
            size="sm"
            className={layoutStyles.keywordSelect}
            classNamePrefix="keyword-select"
            isMulti
            isClearable={false}
            name="tabs"
            value={keywords || []}
            onChange={handleChange}
            onInputChange={handleInputChange}
            inputValue={inputState}
            inputId="keyword-input-id"
            formatCreateLabel={(input: string) => `Add "${input}"`}
            onKeyDown={handleKeyDown}
            options={[]}
            placeholder={placeholder}
            noOptionsMessage={() => null}
            isValidNewOption={isValidNewOption}
            // Rendered in place rather than portaled to the body. The popover
            // surface sets no overflow, so nothing clips the menu, and staying
            // inside it means the menu inherits the surface's stacking context
            // instead of needing a z-index that out-bids it. It also stops
            // Radix counting a click on the menu as an interaction outside the
            // popover.
            menuPortalTarget={null}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              MultiValueRemove: KeywordRemove,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const FreeTextFilterForm = ({
  filter,
  closeMenu,
  onSave,
  onBack,
  isNewInstance,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
}: FreeTextFilterProps) => {
  const [keywords, setKeywords] = useState<readonly KeywordOption[]>([]);
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  const location = useLocation();
  const trackFilterReset = useTrackFilterReset();
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const [isNotEnabled, setIsNotEnabled] = useState<boolean>(
    isNotEnabledProp ?? filter.isNot ?? false
  );

  const handleIsNotChange = (isNot: boolean) => {
    setIsNotEnabled(isNot);
    if (onIsNotEnabledChange) {
      onIsNotEnabledChange(isNot);
    }
  };

  useEffect(() => {
    const existingKeywords = filter.text;
    setKeywords(
      existingKeywords.map((keyword: string) => ({
        label: keyword,
        value: keyword,
      }))
    );
    setIsCurrent(filter.isCurrent ?? true);
  }, [filter]);

  // Kept apart from the keyword resync: switching any-of/none-of changes the
  // prop, and must not throw away what has been typed but not saved.
  useEffect(() => {
    setIsNotEnabled(isNotEnabledProp ?? filter.isNot ?? false);
  }, [filter, isNotEnabledProp]);

  const handleClearSelections = () => {
    trackFilterReset(filter, { selectionCountBefore: keywords.length });
    setKeywords([]);
  };

  const isAddDisabled = keywords.length === 0;

  const handleAddFilter = () => {
    // Filter out whitespace-only items
    const keywordValues = keywords
      .map((keyword) => keyword.value.trim())
      .filter((value) => value.length > 0);

    if (keywordValues.length === 0) {
      return;
    }

    const nextFilter: FreeTextFilter = {
      ...filter,
      text: keywordValues,
      isCurrent,
      isNot: isNotEnabled,
    };
    onSave(nextFilter);
    closeMenu();
  };

  const placeholder =
    filter.id === FilterId.KEYWORDS
      ? 'e.g. linux, hardware, firewall, LAN...'
      : 'e.g. software engineer, data scientist...';

  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  const addLabel = isNewInstance || filter.text.length === 0 ? 'Add' : 'Update';

  return (
    <div className={layoutStyles.form}>
      <div>
        <div onClick={(e) => e.stopPropagation()}>
          <KeywordSelect
            keywords={keywords}
            onKeywordsChange={setKeywords}
            placeholder={placeholder}
            isNot={isNotEnabled}
            onIsNotChange={handleIsNotChange}
            showNotToggle={showNotToggle}
          />
          {showToggle && (
            <div className={layoutStyles.toggleSection}>
              <ToggleSelect
                filterName={filter.name}
                onChange={(e: number) => setIsCurrent(e === 0)}
                initialIsCurrent={isCurrent}
              />
            </div>
          )}
        </div>
      </div>
      <div>
        <FilterSelectFooter
          onClearSelections={handleClearSelections}
          onClose={closeMenu}
          onAdd={handleAddFilter}
          addLabel={addLabel}
          isAddDisabled={isAddDisabled}
          onBack={onBack}
        />
      </div>
    </div>
  );
};
